/**
 * The scorer end of the TournamentRoom WebSocket, and the queue drain.
 *
 * Separate from tournamentRoom.js, which is the admin's: that one asks for a
 * whole-tournament snapshot and only reads. This one is authorised by a
 * pairing token rather than a session, is scoped to one board, and writes.
 *
 * THE DRAIN IS THE ONLY SENDER. Nothing else in the app puts a scoring event
 * on this socket. It walks the queue in order, sends one, waits for the room's
 * answer, and moves on — so ordering is preserved without a sequence number
 * (a WebSocket delivers in order) and so a failure stops the queue rather than
 * letting later visits overtake the one that is stuck.
 *
 * NON-REENTRANT, and the flag is at module scope rather than in a ref. V1 had
 * four callers — an interval, the online event, a manual button and a jam
 * recovery path — with no guard at all. Two overlapping runs each read the
 * queue into a local snapshot, both submitted everything, and whichever
 * finished last wrote its snapshot back, silently undoing the other's
 * progress. Identity makes the double submit harmless now; the clobbered
 * queue would not fix itself.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  readQueue, acknowledge, recordAttempt, subscribe,
} from './scorerQueue.js';

/** Matches tournamentRoom.js. A room that is simply not running is not an error. */
const RECONNECT_MS = 3000;

/** The room's own HEARTBEAT_MS. Answered by the runtime without waking it. */
const PING_MS = 30_000;

/** How long one queued item may sit in flight before the drain gives up on it. */
const ACK_TIMEOUT_MS = 8000;

/** One drain at a time, across every caller and every remount. */
let draining = false;

/**
 * Live board state for one paired scorer.
 *
 * Returns the room's own view of the board — never a locally mutated copy.
 * Projecting the unsent queue over it is the caller's job, so that the two
 * inputs to the screen stay separable and a sync can replace one without
 * disturbing the other.
 */
export function useScorerRoom(session) {
  const { venueId, tournamentId, boardNumber, token } = session ?? {};

  const [status, setStatus] = useState('connecting');
  const [board, setBoard]   = useState(null);
  const [paired, setPaired] = useState(true);
  const [queue, setQueue]   = useState(() =>
    (tournamentId != null ? readQueue(tournamentId, boardNumber) : []));

  const socketRef  = useRef(null);
  const retryRef   = useRef(null);
  const closedRef  = useRef(false);
  const pendingRef = useRef(null);   // the item the drain is waiting on
  const reportedRef = useRef(0);     // last unsynced depth told to the room

  // Storage is the source of truth for the queue; this keeps React level with
  // it no matter which module did the writing.
  useEffect(() => subscribe(() => {
    if (tournamentId != null) setQueue(readQueue(tournamentId, boardNumber));
  }), [tournamentId, boardNumber]);

  const send = useCallback((message) => {
    const socket = socketRef.current;
    if (socket?.readyState !== WebSocket.OPEN) return false;

    try {
      socket.send(JSON.stringify(message));
      return true;
    } catch {
      return false;
    }
  }, []);

  /**
   * Walks the queue until it is empty or something stops it.
   *
   * Every exit path clears `draining`, including the ones that throw — a flag
   * left set would wedge the board permanently, which is a worse failure than
   * the one it guards against.
   */
  const drain = useCallback(async () => {
    if (draining) return;
    if (socketRef.current?.readyState !== WebSocket.OPEN) return;

    draining = true;

    try {
      for (;;) {
        const items = readQueue(tournamentId, boardNumber);
        if (!items.length) break;
        if (socketRef.current?.readyState !== WebSocket.OPEN) break;

        const item = items[0];

        const settled = await new Promise((resolve) => {
          const timer = setTimeout(() => resolve({ ok: false, reason: 'timeout' }), ACK_TIMEOUT_MS);

          pendingRef.current = {
            clientId: item.clientId,
            finish: (outcome) => { clearTimeout(timer); resolve(outcome); },
          };

          const delivered = send({ ...item.payload, type: item.type, clientId: item.clientId });
          if (!delivered) {
            clearTimeout(timer);
            resolve({ ok: false, reason: 'offline' });
          }
        });

        pendingRef.current = null;

        if (settled.ok) {
          acknowledge(tournamentId, boardNumber, item.clientId);
          continue;
        }

        // Stop on the first failure rather than skipping past it. A later
        // visit landing before an earlier one would renumber the leg wrongly,
        // and a queue that silently steps over its own failures is how V1
        // came to drop visits while reporting success.
        recordAttempt(tournamentId, boardNumber, item.clientId);
        break;
      }
    } finally {
      draining = false;
    }
  }, [tournamentId, boardNumber, send]);

  // Tell the room the depth, but only when it moves. A heartbeat republishing
  // an unchanged zero every thirty seconds would wake the Durable Object all
  // night to say nothing.
  useEffect(() => {
    if (status !== 'connected') return;
    if (queue.length === reportedRef.current) return;

    if (send({ type: 'heartbeat', unsynced: queue.length })) {
      reportedRef.current = queue.length;
    }
  }, [queue.length, status, send]);

  useEffect(() => {
    if (venueId == null || tournamentId == null || !token) return undefined;

    closedRef.current = false;

    const connect = () => {
      if (closedRef.current) return;

      const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url =
        `${scheme}//${window.location.host}` +
        `/api/venues/${venueId}/tournaments/${tournamentId}/live` +
        `?type=scorer&token=${encodeURIComponent(token)}`;

      let socket;
      try {
        socket = new WebSocket(url);
      } catch {
        setStatus('disconnected');
        return;
      }

      socketRef.current = socket;

      socket.onopen = () => setStatus('connected');

      socket.onmessage = (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }

        const waiting = pendingRef.current;

        switch (message.type) {
          case 'connected':
          case 'paired':
            setPaired(message.paired !== false);
            if (message.board) setBoard(message.board);
            // A reconnect is the moment the queue can move again.
            reportedRef.current = -1;
            drain();
            return;

          case 'sync_state':
            if (message.board) setBoard(message.board);

            // A recorded visit is acknowledged HERE, not when the room said it
            // had it. The queue overlay and the board are the two halves of
            // what is on screen; dropping the item the moment `visit_recorded`
            // arrives leaves a window — one round trip — where NEITHER holds
            // the visit, and the whole projection snaps back to the pre-submit
            // state and then forward again. It is visible as the score and the
            // active-player highlight double-flicking on every throw.
            //
            // setBoard above and the setQueue that `acknowledge` triggers land
            // in the same React batch, so the handover is one render.
            if (waiting?.recorded) waiting.finish({ ok: true });
            return;

          case 'unpaired':
            // The board was handed to another device, or the pairing aged out.
            // Scoring stops here; Scorer.jsx sends the marker back to /board.
            setPaired(false);
            return;

          case 'visit_recorded':
            // Marked, not finished. The item stays in the queue — and so in the
            // projection — until the sync below brings back a board that holds
            // it. `replayed` counts as recorded too: it means the room already
            // had this visit, which is precisely what the id is for.
            if (waiting && message.clientId === waiting.clientId) {
              waiting.recorded = true;
            }
            send({ type: 'sync' });
            return;

          case 'visit_held':
            // Accepted, not applied: an admin will place or discard it. The
            // marker is told nothing — the darts were thrown and play carries
            // on — and the item must leave the queue or it would retry for
            // ever.
            //
            // Acknowledged immediately, unlike a recorded visit, because no
            // board update is coming: the room deliberately did not store it.
            // The consequence is that this score DOES disappear from the
            // marker's screen when the overlay drops it. That is the truth of
            // what happened, and it is rare enough to leave alone for now, but
            // it is the same shape as the double-flick and worth revisiting if
            // it is ever seen in a live room.
            if (waiting && message.clientId === waiting.clientId) {
              waiting.finish({ ok: true });
            }
            return;

          case 'visit_deleted':
          case 'visit_deleted_ack':
            if (waiting) waiting.finish({ ok: true });
            send({ type: 'sync' });
            return;

          case 'leg_completed':
          case 'match_started':
          case 'match_completed':
            send({ type: 'sync' });
            return;

          case 'error':
            // The drain is serial, so an error while an item is in flight
            // belongs to that item.
            if (waiting) waiting.finish({ ok: false, reason: message.message });
            return;

          default:
        }
      };

      socket.onclose = () => {
        if (closedRef.current) return;
        setStatus('disconnected');
        // Anything in flight is lost with the socket; unblock the drain so it
        // can restart cleanly rather than sitting out its timeout.
        pendingRef.current?.finish({ ok: false, reason: 'closed' });
        pendingRef.current = null;
        retryRef.current = setTimeout(connect, RECONNECT_MS);
      };

      socket.onerror = () => socket.close();
    };

    connect();

    const ping = setInterval(() => send({ type: 'ping' }), PING_MS);

    return () => {
      closedRef.current = true;
      clearInterval(ping);
      clearTimeout(retryRef.current);
      socketRef.current?.close();
    };
  }, [venueId, tournamentId, token, send, drain]);

  // Anything newly queued while connected goes out immediately.
  useEffect(() => {
    if (status === 'connected' && queue.length) drain();
  }, [queue.length, status, drain]);

  return { status, board, paired, queue, drain, send };
}
